/**
 * Formatting utilities for subscription components
 * All text is in Polish to match the application
 */

/**
 * Format currency with Polish locale
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: 'pln')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currency = 'pln') => {
    return new Intl.NumberFormat('pl-PL', {
        style: 'currency',
        currency: currency.toUpperCase()
    }).format(amount);
};

/**
 * Format billing interval to Polish
 * @param {string} interval - Interval (day, week, month, year)
 * @returns {string} Polish interval name
 */
export const formatInterval = (interval) => {
    const intervalMap = {
        day: 'dzień',
        week: 'tydzień',
        month: 'miesiąc',
        year: 'rok'
    };
    return intervalMap[interval] || interval;
};

/**
 * Format date to Polish locale
 * @param {string|Date} dateString - Date to format
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
    if (!dateString) return 'Nie określono';
    return new Date(dateString).toLocaleDateString('pl-PL', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
};

/**
 * Get subscription status text in Polish
 * @param {string} status - Status code
 * @returns {string} Polish status text
 */
export const getSubscriptionStatusText = (status) => {
    const statusMap = {
        'ACTIVE': 'Aktywna',
        'TRIAL': 'Okres próbny',
        'EXPIRED': 'Wygasła',
        'CANCELED': 'Anulowana',
        'UNPAID': 'Nieopłacona',
        'PAUSED': 'Wstrzymana',
        'NONE': 'Brak subskrypcji',
        'NOT_FOUND': 'Nie znaleziono',
        'ERROR': 'Błąd'
    };
    return statusMap[status] || status || 'Nieznany';
};

/**
 * Get Polish text for days remaining
 * @param {number} days - Number of days
 * @returns {string} Polish text for days
 */
export const getDaysText = (days) => {
    if (days === 1) return 'dzień';
    if (days >= 2 && days <= 4) return 'dni';
    return 'dni';
};

/**
 * Calculate days between two dates
 * @param {Date|string} startDate - Start date
 * @param {Date|string} endDate - End date
 * @returns {number} Number of days (rounded up)
 */
export const calculateDaysRemaining = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = end - start;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};
